import { ref, get, set, remove, update } from "firebase/database";
import { dbAsistencia } from "../firebase";

const NOTICE_PATH = "institution_notices";
const LEGACY_ID = "__legacy__";

export const NOTICE_ACTIONS = ["add_widget", "open_url"];
export const NOTICE_PLATFORMS = ["android", "ios", "web"];

// Campos que puede tener el aviso legacy (objeto plano en la raíz de institution_notices)
const LEGACY_KEYS = [
  "title",
  "body",
  "level",
  "enabled",
  "startAt",
  "endAt",
  "createdAt",
  "recipientIds",
  "office",
  "offices",
  "designation",
  "designations",
  "videoUrl",
  "posterUrl",
  "action",
  "actionLabel",
  "actionUrl",
  "platforms",
];
const LEGACY_KEY_SET = new Set(LEGACY_KEYS);

function cleanList(value) {
  return Array.isArray(value) ? value.map((x) => String(x || "").trim()).filter(Boolean) : [];
}

function normalizeMedia(value = {}) {
  const action = NOTICE_ACTIONS.includes(value?.action) ? value.action : "";
  return {
    videoUrl: String(value?.videoUrl || ""),
    posterUrl: String(value?.posterUrl || ""),
    action,
    actionLabel: String(value?.actionLabel || ""),
    actionUrl: action === "open_url" ? String(value?.actionUrl || "") : "",
    platforms: cleanList(value?.platforms).filter((p) => NOTICE_PLATFORMS.includes(p)),
  };
}

// Campos opcionales nuevos; null elimina la clave en RTDB al actualizar
function buildMediaFields(payload = {}) {
  const media = normalizeMedia(payload);
  const platforms = media.action === "add_widget" ? ["android"] : media.platforms;
  return {
    videoUrl: media.videoUrl.trim() || null,
    posterUrl: media.posterUrl.trim() || null,
    action: media.action || null,
    actionLabel: media.action ? media.actionLabel.trim() || null : null,
    actionUrl: media.action === "open_url" ? media.actionUrl.trim() || null : null,
    platforms: platforms.length ? platforms : null,
  };
}

function withoutNulls(obj) {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== null && v !== undefined));
}

function normalizeNotice(id, value = {}) {
  const normalizedOffices = Array.isArray(value?.offices)
    ? value.offices.map((x) => String(x || "").trim()).filter(Boolean)
    : typeof value?.office === "string" && value.office.trim()
      ? [value.office.trim()]
      : [];
  const normalizedDesignations = Array.isArray(value?.designations)
    ? value.designations.map((x) => String(x || "").trim()).filter(Boolean)
    : typeof value?.designation === "string" && value.designation.trim()
      ? [value.designation.trim()]
      : [];
  return {
    id,
    title: String(value?.title || ""),
    body: String(value?.body || ""),
    level: String(value?.level || "info"),
    enabled: value?.enabled !== false,
    startAt: Number(value?.startAt || 0),
    endAt: Number(value?.endAt || 0),
    createdAt: Number(value?.createdAt || 0),
    recipientIds: Array.isArray(value?.recipientIds) ? value.recipientIds : [],
    office: normalizedOffices.length === 1 ? normalizedOffices[0] : "",
    offices: normalizedOffices,
    designation: normalizedDesignations.length === 1 ? normalizedDesignations[0] : "",
    designations: normalizedDesignations,
    ...normalizeMedia(value),
  };
}

export async function listarInstitutionNotices() {
  const snap = await get(ref(dbAsistencia, NOTICE_PATH));
  if (!snap.exists()) return [];

  const data = snap.val();
  if (!data || typeof data !== "object") return [];

  const rows = [];

  // Compatibilidad: estructura vieja (objeto plano en la raíz)
  if (typeof data.body === "string" || typeof data.title === "string") {
    rows.push({ ...normalizeNotice(LEGACY_ID, data), isLegacy: true });
  }

  // Estructura nueva: institution_notices/{id}
  for (const [id, value] of Object.entries(data)) {
    if (!value || typeof value !== "object") continue;
    if (LEGACY_KEY_SET.has(id)) continue;
    rows.push(normalizeNotice(id, value));
  }

  rows.sort((a, b) => Number(b.createdAt || b.startAt || 0) - Number(a.createdAt || a.startAt || 0));
  return rows;
}

export async function crearInstitutionNotice(payload) {
  const now = Date.now();
  const id = `notice_${now}`;
  const notice = {
    title: String(payload?.title || "").trim(),
    body: String(payload?.body || "").trim(),
    level: String(payload?.level || "info").trim(),
    enabled: payload?.enabled !== false,
    startAt: Number(payload?.startAt || now),
    endAt: Number(payload?.endAt || now + 86400000),
    createdAt: now,
    recipientIds: Array.isArray(payload?.recipientIds) ? payload.recipientIds : [],
    offices: Array.isArray(payload?.offices)
      ? payload.offices.map((x) => String(x || "").trim()).filter(Boolean)
      : [],
    designations: Array.isArray(payload?.designations)
      ? payload.designations.map((x) => String(x || "").trim()).filter(Boolean)
      : [],
    ...withoutNulls(buildMediaFields(payload)),
  };
  if (notice.offices.length === 1) {
    notice.office = notice.offices[0];
  }
  if (notice.designations.length === 1) {
    notice.designation = notice.designations[0];
  }

  await set(ref(dbAsistencia, `${NOTICE_PATH}/${id}`), notice);
  if (notice.enabled) {
    await inactivarOtrasNotificaciones(id);
  }
  return { id, ...notice };
}

export async function actualizarInstitutionNotice(id, payload) {
  const updates = {
    title: String(payload?.title || "").trim(),
    body: String(payload?.body || "").trim(),
    level: String(payload?.level || "info").trim(),
    enabled: !!payload?.enabled,
    startAt: Number(payload?.startAt || Date.now()),
    endAt: Number(payload?.endAt || Date.now()),
    recipientIds: Array.isArray(payload?.recipientIds) ? payload.recipientIds : [],
    offices: Array.isArray(payload?.offices)
      ? payload.offices.map((x) => String(x || "").trim()).filter(Boolean)
      : [],
    designations: Array.isArray(payload?.designations)
      ? payload.designations.map((x) => String(x || "").trim()).filter(Boolean)
      : [],
    ...buildMediaFields(payload),
  };
  updates.office = updates.offices.length === 1 ? updates.offices[0] : "";
  updates.designation = updates.designations.length === 1 ? updates.designations[0] : "";

  if (id === LEGACY_ID) {
    await update(ref(dbAsistencia, NOTICE_PATH), updates);
    if (updates.enabled) {
      await inactivarOtrasNotificaciones(LEGACY_ID);
    }
    return { id: LEGACY_ID, ...withoutNulls(updates) };
  }

  await update(ref(dbAsistencia, `${NOTICE_PATH}/${id}`), updates);
  if (updates.enabled) {
    await inactivarOtrasNotificaciones(id);
  }
  return { id, ...withoutNulls(updates) };
}

export async function eliminarInstitutionNotice(id) {
  if (id === LEGACY_ID) {
    const rootRef = ref(dbAsistencia, NOTICE_PATH);
    const snap = await get(rootRef);
    if (!snap.exists()) return;
    const data = snap.val() || {};
    const cleaned = { ...data };
    for (const key of LEGACY_KEYS) delete cleaned[key];
    await set(rootRef, cleaned);
    return;
  }
  await remove(ref(dbAsistencia, `${NOTICE_PATH}/${id}`));
}

async function inactivarOtrasNotificaciones(activeId) {
  const snap = await get(ref(dbAsistencia, NOTICE_PATH));
  if (!snap.exists()) return;
  const data = snap.val() || {};

  const updates = {};

  // Compatibilidad estructura legacy (objeto plano en institution_notices)
  const hasLegacyShape =
    typeof data?.title === "string" ||
    typeof data?.body === "string" ||
    typeof data?.enabled === "boolean";
  if (hasLegacyShape && activeId !== LEGACY_ID && data.enabled === true) {
    updates[`${NOTICE_PATH}/enabled`] = false;
  }

  for (const [id, value] of Object.entries(data)) {
    if (!value || typeof value !== "object") continue;
    if (LEGACY_KEY_SET.has(id)) continue;
    if (id !== activeId && value.enabled === true) {
      updates[`${NOTICE_PATH}/${id}/enabled`] = false;
    }
  }

  if (Object.keys(updates).length > 0) {
    await update(ref(dbAsistencia), updates);
  }
}
